/**
 * MediaRecorder пишет WebM без длительности (Segment Info без Duration) — в плеере не работает перемотка.
 * Длительность известна по таймеру записи: дописываем элемент Duration в Info, кластеры не трогаем.
 * Вставка сдвигает всё после Info: смещения в SeekHead поправляются, а с индексом Cues
 * (позиции кластеров) или при Info дальше начала файла файл остаётся как есть.
 */

const ID_EBML = 0x1a45dfa3
const ID_SEGMENT = 0x18538067
const ID_SEEK_HEAD = 0x114d9b74
const ID_SEEK = 0x4dbb
const ID_SEEK_ID = 0x53ab
const ID_SEEK_POSITION = 0x53ac
const ID_INFO = 0x1549a966
const ID_CUES = 0x1c53bb6b
const ID_CLUSTER = 0x1f43b675
const ID_TIMECODE_SCALE = 0x2ad7b1
const ID_DURATION = 0x4489

const HEAD_BYTES = 256 * 1024

function vintLength(first: number): number {
  for (let i = 0; i < 8; i++) if (first & (0x80 >> i)) return i + 1
  return 0
}

function readId(buf: Uint8Array, pos: number): { id: number; len: number } | null {
  const len = pos < buf.length ? vintLength(buf[pos]) : 0
  if (!len || len > 4 || pos + len > buf.length) return null
  let id = 0
  for (let i = 0; i < len; i++) id = id * 256 + buf[pos + i]
  return { id, len }
}

function readSize(buf: Uint8Array, pos: number): { size: number; len: number; unknown: boolean } | null {
  const len = pos < buf.length ? vintLength(buf[pos]) : 0
  if (!len || pos + len > buf.length) return null
  const mask = 0xff >> len
  let size = buf[pos] & mask
  let unknown = size === mask
  for (let i = 1; i < len; i++) {
    size = size * 256 + buf[pos + i]
    if (buf[pos + i] !== 0xff) unknown = false
  }
  return { size, len, unknown }
}

function encodeSize(value: number, len: number): Uint8Array | null {
  if (value >= 2 ** (7 * len) - 1) return null
  const out = new Uint8Array(len)
  let v = value
  for (let i = len - 1; i >= 0; i--) {
    out[i] = v % 256
    v = Math.floor(v / 256)
  }
  out[0] |= 0x80 >> (len - 1)
  return out
}

function readUint(buf: Uint8Array, start: number, len: number): number {
  let v = 0
  for (let i = 0; i < len; i++) v = v * 256 + buf[start + i]
  return v
}

/** Blob записи с длительностью durationMs в заголовке; при любой неожиданности — исходный blob */
export async function fixWebmDuration(blob: Blob, durationMs: number): Promise<Blob> {
  if (!(durationMs > 0)) return blob
  try {
    const head = new Uint8Array(await blob.slice(0, Math.min(blob.size, HEAD_BYTES)).arrayBuffer())
    const ebml = readId(head, 0)
    const ebmlSize = ebml?.id === ID_EBML ? readSize(head, ebml.len) : null
    if (!ebml || !ebmlSize || ebmlSize.unknown) return blob
    const segPos = ebml.len + ebmlSize.len + ebmlSize.size
    const seg = readId(head, segPos)
    if (seg?.id !== ID_SEGMENT) return blob
    const segSizePos = segPos + seg.len
    const segSize = readSize(head, segSizePos)
    if (!segSize) return blob
    let seekHead: { start: number; end: number } | null = null
    let pos = segSizePos + segSize.len
    while (pos < head.length) {
      const el = readId(head, pos)
      const sz = el ? readSize(head, pos + el.len) : null
      if (!el || !sz || sz.unknown || el.id === ID_CLUSTER) return blob
      const bodyStart = pos + el.len + sz.len
      const end = bodyStart + sz.size
      if (el.id === ID_SEEK_HEAD) {
        if (seekHead || end > head.length) return blob
        seekHead = { start: bodyStart, end }
      }
      if (el.id === ID_INFO) {
        if (end > head.length) return blob
        return patchInfo(blob, head, { segSizePos, segSize, infoPos: pos, bodyStart, end, seekHead }, durationMs)
      }
      pos = end
    }
    return blob
  } catch {
    return blob
  }
}

/** Сдвинуть на delta позиции SeekHead (от начала данных Segment) дальше `after`; false — так нельзя */
function shiftSeekHead(buf: Uint8Array, range: { start: number; end: number }, after: number, delta: number): boolean {
  for (let p = range.start; p < range.end; ) {
    const seek = readId(buf, p)
    const ssz = seek ? readSize(buf, p + seek.len) : null
    if (!seek || !ssz || ssz.unknown) return false
    const start = p + seek.len + ssz.len
    p = start + ssz.size
    if (seek.id !== ID_SEEK) continue
    let target = 0
    let position: { start: number; len: number } | null = null
    for (let q = start; q < p; ) {
      const el = readId(buf, q)
      const sz = el ? readSize(buf, q + el.len) : null
      if (!el || !sz || sz.unknown) return false
      const body = q + el.len + sz.len
      if (el.id === ID_SEEK_ID) target = readUint(buf, body, Math.min(sz.size, 4))
      if (el.id === ID_SEEK_POSITION) position = { start: body, len: sz.size }
      q = body + sz.size
    }
    if (target === ID_CUES) return false
    if (!position || position.len < 1 || position.len > 6) continue
    const value = readUint(buf, position.start, position.len)
    if (value <= after) continue
    let next = value + delta
    if (next >= 2 ** (8 * position.len)) return false
    for (let i = position.len - 1; i >= 0; i--) {
      buf[position.start + i] = next % 256
      next = Math.floor(next / 256)
    }
  }
  return true
}

function patchInfo(
  blob: Blob,
  head: Uint8Array,
  at: { segSizePos: number; segSize: { size: number; len: number; unknown: boolean }; infoPos: number; bodyStart: number; end: number; seekHead: { start: number; end: number } | null },
  durationMs: number,
): Blob {
  let scale = 1_000_000
  let duration: { start: number; len: number } | null = null
  for (let p = at.bodyStart; p < at.end; ) {
    const el = readId(head, p)
    const sz = el ? readSize(head, p + el.len) : null
    if (!el || !sz || sz.unknown) return blob
    const start = p + el.len + sz.len
    if (el.id === ID_TIMECODE_SCALE && sz.size >= 1 && sz.size <= 8) scale = readUint(head, start, sz.size) || scale
    if (el.id === ID_DURATION) duration = { start, len: sz.size }
    p = start + sz.size
  }
  const ticks = (durationMs * 1_000_000) / scale

  if (duration) {
    if (duration.len !== 4 && duration.len !== 8) return blob
    const patched = head.slice(0, at.end)
    const view = new DataView(patched.buffer)
    if (duration.len === 8) view.setFloat64(duration.start, ticks)
    else view.setFloat32(duration.start, ticks)
    return new Blob([patched, blob.slice(at.end)], { type: blob.type })
  }

  const element = new Uint8Array(11)
  element.set([0x44, 0x89, 0x88])
  new DataView(element.buffer).setFloat64(3, ticks)
  const body = new Uint8Array(at.end - at.bodyStart + element.length)
  body.set(head.subarray(at.bodyStart, at.end))
  body.set(element, at.end - at.bodyStart)
  const infoSize = encodeSize(body.length, 8)!
  const info = new Uint8Array(4 + infoSize.length + body.length)
  info.set([0x15, 0x49, 0xa9, 0x66])
  info.set(infoSize, 4)
  info.set(body, 4 + infoSize.length)

  const delta = info.length - (at.end - at.infoPos)
  const prefix = head.slice(0, at.infoPos)
  const segData = at.segSizePos + at.segSize.len
  if (at.seekHead && !shiftSeekHead(prefix, at.seekHead, at.infoPos - segData, delta)) return blob
  let segSize: Uint8Array = prefix.subarray(at.segSizePos, segData)
  if (!at.segSize.unknown) {
    const next = encodeSize(at.segSize.size + delta, at.segSize.len)
    if (!next) return blob
    segSize = next
  }
  return new Blob([prefix.subarray(0, at.segSizePos), segSize, prefix.subarray(segData), info, blob.slice(at.end)], { type: blob.type })
}
